/**
 * LearnScan.jsx — task-driven speed-reading. User is shown a specific
 * "find this fact" question BEFORE the passage. Timer starts as the
 * passage appears; user taps "Found it!" the moment they can answer.
 * The full comprehension quiz then confirms they actually saw the
 * answer instead of guessing. Trains the skill of matching reading
 * speed to purpose — a separate leaderboard from the Deep Reader path.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Play, ArrowLeft, ChevronDown, Clock, Target } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useTicker = (running) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [running]);
  return now;
};

const LearnScan = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [state, setState] = useState('setup'); // setup|task|scanning|quiz|done
  const [scanQuestionIdx, setScanQuestionIdx] = useState(0);
  const startedAt = useRef(0);
  const scanMs = useRef(0);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) { setPassages([custom]); return; }
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/passages`);
        if (r.ok) { const d = await r.json(); setPassages(d.passages || []); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];
  const now = useTicker(state === 'scanning');
  const elapsedMs = state === 'scanning' ? Math.max(0, now - startedAt.current) : scanMs.current;

  const pickScanQuestion = () => {
    // Pick a random question from the passage's comprehension bank as
    // the "find this fact" task. Storing the index so we can highlight
    // it as the primary question in the follow-up quiz.
    if (!passage) return 0;
    return Math.floor(Math.random() * passage.questions.length);
  };

  const start = () => {
    setScanQuestionIdx(pickScanQuestion());
    setState('task');
  };
  const beginScan = () => {
    startedAt.current = Date.now();
    setState('scanning');
  };
  const foundIt = () => {
    scanMs.current = Date.now() - startedAt.current;
    setState('quiz');
  };
  const reset = () => { setState('setup'); scanMs.current = 0; };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-scan-loading">
        Loading passages…
      </main>
    );
  }

  const scanQ = passage.questions[scanQuestionIdx];
  const secs = Math.floor(elapsedMs / 1000);
  const ms = String(Math.floor((elapsedMs % 1000) / 100));

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-scan-page">
      <button onClick={() => navigate('/learn')} className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <Search className="w-6 h-6 text-cyan-300" />
        <div>
          <p className="eyebrow text-cyan-300 mb-0.5">Drill · Skim / Scan</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Find the fact<span className="text-cyan-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400/60"
                data-testid="learn-scan-passage-select"
              >
                {passages.map((p, i) => (<option key={p.passage_id} value={i}>{p.title} — {p.author} ({p.word_count} words)</option>))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          <p className="text-xs text-white/50 leading-relaxed">
            You&apos;ll be shown a specific fact-finding task, then the passage. Tap <span className="text-cyan-300 font-black">Found it!</span> the instant you know the answer — your scan-time is logged. The comprehension quiz confirms you actually saw it.
          </p>
          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-scan-begin"
          >
            <Play className="w-4 h-4" /> Give me a task
          </button>
        </div>
      )}

      {state === 'task' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-gradient-to-br from-cyan-950/60 via-slate-900 to-slate-950 border border-cyan-500/30 p-6" data-testid="learn-scan-task">
            <p className="eyebrow text-cyan-300 mb-2 flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5" /> Your task
            </p>
            <p className="font-display text-2xl font-black text-white tracking-tight leading-tight">
              {scanQ.q}
            </p>
          </div>
          <button
            onClick={beginScan}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-scan-show-passage"
          >
            Show me the passage
          </button>
        </div>
      )}

      {state === 'scanning' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-black/40 border border-cyan-500/30 px-4 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Scan time
            </p>
            <p className="font-display text-3xl font-black text-white tabular-nums" data-testid="learn-scan-timer">
              {secs}.{ms}s
            </p>
          </div>
          <div className="rounded-xl bg-cyan-500/10 border border-cyan-400/30 px-4 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-cyan-300/80 font-black mb-1 flex items-center gap-1.5">
              <Target className="w-3 h-3" /> Find
            </p>
            <p className="text-sm text-white/90 font-black">{scanQ.q}</p>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-cyan-500/20 p-6 text-white/90 leading-relaxed text-base sm:text-lg" data-testid="learn-scan-passage-text">
            {passage.text}
          </div>
          <button
            onClick={foundIt}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-scan-found"
          >
            <Search className="w-4 h-4" /> Found it!
          </button>
        </div>
      )}

      {state === 'quiz' && (
        <div className="space-y-3">
          <p className="text-xs text-white/50">
            Scan time: <span className="text-cyan-300 font-black tabular-nums">{(scanMs.current / 1000).toFixed(1)}s</span> · confirm you got the answer:
          </p>
          <LearnQuiz
            passage={passage}
            drillKind="scan"
            durationMs={scanMs.current}
            metadata={{ scan_ms: scanMs.current, scan_target_q: scanQuestionIdx }}
            onDone={() => setState('done')}
          />
        </div>
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button onClick={reset} className="flex-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-4 py-3 text-sm font-black" data-testid="learn-scan-again">
            Run another drill
          </button>
          <button onClick={() => navigate('/learn')} className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black">
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnScan;
